import SEO from '../components/SEO';
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, TrendingUp, Zap, Shield, Users, Target, BarChart3, Clock, Sparkles, ArrowRight, Award, Globe, DollarSign, Rocket, LineChart, Lock, Headphones, Building2, Workflow, Cloud, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import ProductInquiryForm from '../components/ProductInquiryForm';

const WhySAPBusinessOneTest = () => {
  const stats = [
    { value: "80,000+", label: "Businesses Worldwide", color: "text-blue-600" },
    { value: "170", label: "Countries", color: "text-blue-500" },
    { value: "99.995%", label: "Cloud Uptime", color: "text-emerald-600" },
    { value: "4.9/5", label: "Customer Rating", color: "text-amber-500" }
  ];

  const challenges = [
    { icon: BarChart3, title: "Disconnected Systems", desc: "Multiple spreadsheets and databases that don't communicate" },
    { icon: Clock, title: "Manual Processes", desc: "Time-consuming data entry stealing productivity" },
    { icon: TrendingUp, title: "Limited Visibility", desc: "Decisions based on gut feeling, not real-time data" },
    { icon: DollarSign, title: "Hidden Costs", desc: "Inefficiencies and errors eating profit margins" }
  ];

  const benefits = [
    { icon: Target, title: "Complete Control", desc: "Unified view of finance, sales, inventory, and operations" },
    { icon: LineChart, title: "Real-Time Intelligence", desc: "Live dashboards and AI-powered business insights" },
    { icon: Workflow, title: "Automated Workflows", desc: "Eliminate manual tasks and reduce human error" },
    { icon: Rocket, title: "Unlimited Scale", desc: "Grow from 10 to 1,000+ users seamlessly" },
    { icon: Globe, title: "Global Operations", desc: "Multi-currency, multi-language, multi-company ready" },
    { icon: Cloud, title: "Enterprise Security", desc: "AWS-hosted with 99.995% uptime SLA" }
  ];

  const whyAscarii = [
    { number: "100%", label: "Dedicated to SAP Business One", desc: "Pure focus. Our entire team works on SAP B1 every single day." },
    { number: "15+", label: "Years of Experience", desc: "Deep expertise from hundreds of successful implementations." },
    { number: "4.9/5", label: "Customer Satisfaction", desc: "Our customers love us because we deliver real results." },
    { number: "24/7", label: "UK-Based Support", desc: "Always available when you need guidance or assistance." }
  ];

  const journey = [
    { step: "1", title: "Discovery", desc: "30-minute call to understand your unique challenges", time: "30 mins" },
    { step: "2", title: "Solution Design", desc: "Custom configuration matched to your needs", time: "1-2 weeks" },
    { step: "3", title: "Implementation", desc: "Smooth deployment with training and migration", time: "8-12 weeks" },
    { step: "4", title: "Go-Live", desc: "Dedicated support during your first critical weeks", time: "2-4 weeks" },
    { step: "5", title: "Partnership", desc: "Ongoing optimization as your business evolves", time: "Continuous" }
  ];

  const testimonials = [
    { company: "Hawthorn Trading", metric: "£13k", label: "Q1 Savings", quote: "Better purchasing power through data visibility saved us £13,000 in the first quarter alone." },
    { company: "Abbeydale Direct", metric: "10x", label: "Growth", quote: "SAP B1 took us from small thinking to making all things not only possible, but reality." },
    { company: "Taoglas", metric: "20+", label: "Countries", quote: "Supporting our rapid global expansion in IoT antenna manufacturing across continents." }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Why SAP Business One | Premium Design Test Page"
        description="Test version showcasing modern design for Why SAP Business One page. Premium aesthetic for B2B SaaS positioning."
        keywords="SAP Business One, ERP software, business management"
      />

      {/* Hero Section - Clean, Modern, Spacious */}
      <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Why SAP Business One
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6 leading-[1.1] tracking-tight">
                Stop Juggling Systems.
                <span className="block text-blue-600 mt-2">Start Growing.</span>
              </h1>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed max-w-xl">
                One powerful platform. Complete business visibility. Real-time intelligence. Join 80,000+ businesses worldwide.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
                  Book a Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button variant="outline" className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3 rounded-xl transition-all duration-200">
                  Download Brochure
                </Button>
              </div>
            </motion.div>

            {/* Right: Image - Modern Integration */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.98 }} 
              animate={{ opacity: 1, scale: 1 }} 
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative lg:ml-8"
            >
              {/* Soft decorative accent */}
              <div className="absolute -top-12 -right-12 w-64 h-64 bg-blue-100/40 rounded-full blur-3xl" />
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-emerald-100/30 rounded-full blur-3xl" />
              
              {/* Image container with subtle frame */}
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 rounded-2xl" />
                <img 
                  src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=90" 
                  alt="SAP Business One Dashboard" 
                  className="relative rounded-2xl shadow-xl border border-gray-100/50"
                  loading="eager"
                />
                {/* Floating metric badge */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
                      <Award className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-gray-900">4.9/5</div>
                      <div className="text-xs text-gray-500">Rating</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats - Minimal, Spacious */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div 
                key={i} 
                initial={{ opacity: 0, y: 10 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.08 }} 
                viewport={{ once: true }}
                className="text-center group"
              >
                <div className={`text-4xl md:text-5xl font-bold ${stat.color} mb-2 transition-transform duration-200 group-hover:scale-105`}>
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges - Soft Background, Clean Cards */}
      <section className="py-24 bg-gradient-to-b from-red-50/30 to-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            whileInView={{ opacity: 1, y: 0 }} 
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-50 text-red-700 rounded-full text-sm font-medium mb-4">
              <Target className="w-3.5 h-3.5" />
              Common Challenges
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              You Shouldn't Have to Work This Hard
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These challenges are holding you back from reaching your potential
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {challenges.map((challenge, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 15 }} 
                whileInView={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="group bg-white rounded-xl p-8 border border-red-100/50 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0 group-hover:bg-red-100 transition-colors duration-200">
                    <challenge.icon className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{challenge.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{challenge.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solution - Split Layout with Integrated Image */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            {/* Image Side - Integrated Design */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="relative order-2 lg:order-1"
            >
              {/* Decorative soft accents */}
              <div className="absolute -top-8 -left-8 w-32 h-32 bg-blue-100/30 rounded-full blur-2xl" />
              <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-emerald-100/20 rounded-full blur-2xl" />
              
              {/* Main image with minimal frame */}
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=90" 
                  alt="SAP Business One Dashboard" 
                  className="relative rounded-2xl shadow-xl"
                />
                {/* Subtle overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent rounded-2xl pointer-events-none" />
                
                {/* Floating stat card */}
                <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-lg p-5 border border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-lg bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center">
                      <TrendingUp className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 mb-0.5">Average ROI</div>
                      <div className="text-3xl font-bold text-gray-900">284%</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content Side */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }} 
              whileInView={{ opacity: 1, x: 0 }} 
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                The Solution
              </div>
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 tracking-tight leading-[1.15]">
                One Platform.<br />Complete Control.
              </h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed max-w-prose">
                SAP Business One connects every part of your business into one intelligent system. Real-time visibility, automated workflows, and insights for confident decisions.
              </p>
              
              <div className="space-y-4">
                {[
                  "All business functions connected in real-time",
                  "Automate processes and eliminate data entry errors",
                  "Make data-driven decisions with AI analytics",
                  "Scale effortlessly without system changes",
                  "Secure cloud hosting with 99.995% uptime"
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-blue-600" />
                    </div>
                    <p className="text-gray-700">{item}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits - Clean Grid with Icons */}
      <section className="py-24 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Award className="w-3.5 h-3.5" />
              Key Benefits
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Everything You Need to Succeed
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Enterprise capabilities designed specifically for growing businesses
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                viewport={{ once: true }}
                className="group bg-white rounded-xl p-8 border border-gray-100 shadow-sm hover:shadow-md hover:border-blue-100 transition-all duration-200"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-5 group-hover:bg-blue-100 transition-colors duration-200">
                  <benefit.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Stories - Minimal Cards with Soft Accents */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium mb-4">
              <Award className="w-3.5 h-3.5" />
              Customer Success
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Real Businesses. Real Results.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((story, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-8 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-baseline gap-2 mb-6">
                  <div className="text-4xl font-bold text-blue-600">{story.metric}</div>
                  <div className="text-sm text-gray-500 font-medium">{story.label}</div>
                </div>
                <h3 className="font-semibold text-gray-900 mb-3">{story.company}</h3>
                <p className="text-sm text-gray-600 leading-relaxed italic">"{story.quote}"</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Ascarii - Clean Grid */}
      <section className="py-24 bg-gradient-to-b from-blue-50/30 to-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-sm font-medium mb-4">
              <Headphones className="w-3.5 h-3.5" />
              Why Choose Ascarii
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              Your Success is Our Mission
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {whyAscarii.map((reason, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 border border-blue-100/50 shadow-sm hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start gap-6">
                  <div className="text-3xl font-bold text-blue-600 flex-shrink-0">{reason.number}</div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-2">{reason.label}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{reason.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Implementation Journey - Timeline */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-4">
              <Rocket className="w-3.5 h-3.5" />
              Your Journey
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              From First Call to Full Success
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A proven process designed for your success
            </p>
          </motion.div>

          <div className="space-y-6">
            {journey.map((phase, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                viewport={{ once: true }}
                className="relative"
              >
                <div className="flex items-start gap-6">
                  {/* Step indicator */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center shadow-sm">
                      <span className="text-xl font-bold text-white">{phase.step}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 bg-white rounded-xl p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200">
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <h3 className="text-xl font-semibold text-gray-900">{phase.title}</h3>
                      <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap">
                        {phase.time}
                      </span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{phase.desc}</p>
                  </div>
                </div>

                {/* Connecting line */}
                {i < journey.length - 1 && (
                  <div className="absolute left-7 top-14 bottom-0 w-px bg-gradient-to-b from-blue-200 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA - Minimal, Clean */}
      <section className="py-24 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        {/* Subtle pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ 
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '32px 32px' 
          }} />
        </div>

        <div className="container mx-auto px-6 lg:px-8 max-w-4xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Transform Your Business?
            </h2>
            <p className="text-xl text-blue-100 mb-12 max-w-2xl mx-auto leading-relaxed">
              Join 80,000+ businesses worldwide. Let's discuss how we can help you grow.
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-3 rounded-xl shadow-sm hover:shadow-md transition-all duration-200">
                Book a Discovery Call
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-3 rounded-xl border border-blue-400/30 transition-all duration-200">
                Download Brochure
              </Button>
            </div>

            {/* Trust badges - minimal */}
            <div className="flex flex-wrap justify-center gap-8 pt-8 border-t border-white/10">
              {[
                { icon: Award, text: "SAP Premier" },
                { icon: Shield, text: "AWS Advanced" },
                { icon: Users, text: "15+ Years" }
              ].map((badge, i) => (
                <div key={i} className="flex items-center gap-2 text-white/90">
                  <badge.icon className="w-5 h-5" />
                  <span className="text-sm font-medium">{badge.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Form - Spacious, Clean */}
      <section className="py-24 bg-gradient-to-b from-slate-50/50 to-white">
        <div className="container mx-auto px-6 lg:px-8 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Get Started Today</h2>
            <p className="text-lg text-gray-600">We'll respond within one business day</p>
          </div>
          <div className="bg-white rounded-2xl p-8 lg:p-10 border border-gray-100 shadow-sm">
            <ProductInquiryForm productName="SAP Business One" />
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhySAPBusinessOneTest;
